<template>
  <v-dialog :value="value" max-width="650px" persistent @input="$emit('input', $event)">
    <v-card v-if="falta">
      <v-card-title class="headline grey lighten-3 d-flex justify-space-between align-center">
        <span>Detalhes da Falta Justificada</span>
        <v-chip
          :color="statusColor(falta.status)"
          dark
          small
          class="font-weight-bold text-uppercase"
        >
          {{ falta.status || "Pendente" }}
        </v-chip>
      </v-card-title>

      <v-card-text class="pt-4">
        <v-row dense>
          <v-col cols="12" md="6">
            <div class="caption grey--text">Colaborador</div>
            <div class="body-1 font-weight-bold">
              {{ falta.colaborador_nome || falta.user?.name || "N/A" }}
            </div>
          </v-col>

          <v-col cols="12" md="6" v-if="falta.colaborador_cpf || falta.user?.cpf">
            <div class="caption grey--text">CPF</div>
            <div class="body-2">
              {{ falta.colaborador_cpf || falta.user?.cpf }}
            </div>
          </v-col>

          <v-col cols="12" md="6" class="mt-2">
            <div class="caption grey--text">Período da Ausência</div>
            <div class="body-2">
              <strong>De:</strong> {{ formatarData(falta.data_inicio) }}
              <strong v-if="falta.data_fim"> até </strong>
              <span v-if="falta.data_fim">{{ formatarData(falta.data_fim) }}</span>
              <span v-if="totalDias > 1" class="grey--text"> ({{ totalDias }} dias)</span>
            </div>
          </v-col>

          <v-col cols="12" md="6" class="mt-2">
            <div class="caption grey--text">Motivo</div>
            <div class="body-2 font-weight-medium">
              {{ falta.motivo }}
            </div>
          </v-col>

          <v-col cols="12" class="mt-2" v-if="falta.observacao">
            <div class="caption grey--text">Observação do Colaborador</div>
            <div class="body-2 pa-2 grey lighten-4 rounded">
              {{ falta.observacao }}
            </div>
          </v-col>

          <v-col cols="12" class="mt-3" v-if="falta.tem_documento || falta.documento_path">
            <v-btn
              color="primary"
              outlined
              small
              :loading="carregandoDocumento"
              @click="abrirComprovante"
            >
              <v-icon left small>mdi-file-document-outline</v-icon>
              Visualizar Comprovante Anexo
            </v-btn>
          </v-col>

          <v-col cols="12">
            <v-divider class="my-3"></v-divider>
          </v-col>

          <!-- Seção de Decisão / Histórico de Avaliação -->
          <v-col cols="12" v-if="falta.status !== 'pendente'">
            <div class="subtitle-2 font-weight-bold mb-1">Avaliação do Gestor</div>
            <div class="body-2">
              <strong>Avaliado por:</strong>
              {{ falta.avaliado_por_nome || falta.avaliador?.name || "Gestor" }}
            </div>
            <div class="body-2" v-if="falta.data_avaliacao">
              <strong>Data da Decisão:</strong> {{ formatarDataHora(falta.data_avaliacao) }}
            </div>
            <div class="body-2 mt-1 pa-2 grey lighten-4 rounded" v-if="falta.observacao_gestor">
              <strong>Parecer:</strong> {{ falta.observacao_gestor }}
            </div>
          </v-col>

          <!-- Formulário de Avaliação (para Gestores quando status == pendente) -->
          <v-col cols="12" v-if="podeAvaliar">
            <div class="subtitle-2 font-weight-bold mb-2">Avaliar Solicitação</div>
            <v-textarea
              v-model="observacaoGestor"
              label="Parecer / Observação da Gestão"
              outlined
              dense
              rows="3"
              placeholder="Digite uma observação para justificativa da decisão..."
            />

            <v-alert v-if="erroAvaliacao" type="error" dense outlined class="mb-2">
              {{ erroAvaliacao }}
            </v-alert>
          </v-col>

          <v-col cols="12" v-if="falta.status === 'pendente' && !podeAvaliar">
            <v-alert type="info" dense outlined class="mt-2">
              Você não possui permissão para avaliar esta falta ou não pode avaliar a sua própria
              solicitação.
            </v-alert>
          </v-col>
        </v-row>
      </v-card-text>

      <v-card-actions class="pa-4 pt-0 justify-end">
        <v-btn text @click="fechar">Fechar</v-btn>

        <template v-if="podeAvaliar">
          <v-btn color="error" outlined :loading="salvando" @click="submeterAvaliacao('reprovado')">
            <v-icon left small>mdi-close-circle</v-icon>
            Reprovar
          </v-btn>

          <v-btn color="success" :loading="salvando" @click="submeterAvaliacao('aprovado')">
            <v-icon left small>mdi-check-circle</v-icon>
            Aprovar
          </v-btn>
        </template>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "FaltaJustificadaDetailDialog",
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    falta: {
      type: Object,
      default: () => null,
    },
  },
  data() {
    return {
      observacaoGestor: "",
      salvando: false,
      carregandoDocumento: false,
      erroAvaliacao: null,
    };
  },
  computed: {
    ...mapGetters("Auth", ["user"]),
    podeAvaliar() {
      if (!this.falta || this.falta.status !== "pendente") return false;
      const userId = this.user?.id;
      const faltaUserId = this.falta.user_id || this.falta.colaborador_user_id;
      // Regra de Ouro: impede auto-avaliação (user.id !== falta.user_id)
      return userId && userId !== faltaUserId;
    },
    totalDias() {
      if (!this.falta?.data_inicio) return 1;
      if (!this.falta?.data_fim) return 1;
      const start = new Date(this.falta.data_inicio);
      const end = new Date(this.falta.data_fim);
      const diffTime = Math.abs(end - start);
      return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    },
  },
  watch: {
    value(val) {
      if (val) {
        this.observacaoGestor = "";
        this.erroAvaliacao = null;
      }
    },
  },
  methods: {
    statusColor(status) {
      switch (status) {
        case "aprovado":
          return "success";
        case "reprovado":
          return "error";
        case "pendente":
        default:
          return "warning";
      }
    },
    formatarData(val) {
      if (!val) return "";
      const datePart = val.split("T")[0];
      const [ano, mes, dia] = datePart.split("-");
      return `${dia}/${mes}/${ano}`;
    },
    formatarDataHora(val) {
      if (!val) return "";
      const d = new Date(val);
      return d.toLocaleString("pt-BR");
    },
    fechar() {
      this.$emit("input", false);
    },
    async abrirComprovante() {
      if (!this.falta?.id) return;
      try {
        this.carregandoDocumento = true;
        if (this.podeAvaliar || this.$can("colaboradores.faltasJustificadas.gerenciar")) {
          await this.$services.colaboradorPontosService.abrirDocumentoFalta(this.falta.id);
        } else {
          await this.$services.colaboradorPontosService.meuDocumentoFalta(this.falta.id);
        }
      } catch (error) {
        this.$handleError(error);
      } finally {
        this.carregandoDocumento = false;
      }
    },
    async submeterAvaliacao(status) {
      this.erroAvaliacao = null;
      try {
        this.salvando = true;
        await this.$services.colaboradorPontosService.avaliarFaltaJustificada(this.falta.id, {
          status,
          observacao_gestor: this.observacaoGestor,
        });

        this.$emit("avaliado", {
          id: this.falta.id,
          status,
          observacao_gestor: this.observacaoGestor,
        });
        this.fechar();
      } catch (error) {
        if (error.response?.data?.message) {
          this.erroAvaliacao = error.response.data.message;
        } else {
          this.$handleError(error);
        }
      } finally {
        this.salvando = false;
      }
    },
  },
};
</script>
